import {
  Box,
  Collapse,
  Stack,
  Typography,
  IconButton,
  ListItemButton,
  List,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, matchPath, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import DelegonLogo from 'assets/Delegon_Main_Logo.png';
import DelegonSmallLogo from 'assets/Delegon_Small_Logo.png';
import DelegonSmallLogoWhite from 'assets/Delegon_Small_Logo_White.png';


import { IconWrapper } from 'components/Icon';
import { VERTICAL_NAV } from '../config';
import { useNavItems } from '../hooks/useNavItems';
import { useActiveNavLink } from 'routes/hooks/useActiveNavLink';
import { paths, ROOT_PATHS } from 'routes';
import { useAuth } from 'lib/auth';
import { useGetCompanyById } from 'features/companies/api/getCompanyById';
import { useNavbar } from 'lib/navbarContext';

const activeLinkStyling = {
  bgcolor: '#19454E!important',
  pl: 2,
};
const defaultLinkStyling = {
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  color: "text.white",
  p: 2,
};

const isPathActive = (path, pathname) => !!path && !!matchPath({ path, end: false }, pathname);

const NavIcon = ({ Icon, closed, iconSize, isSubItem }) => (
  <ListItemIcon display="flex" sx={{ minWidth: 16 }}>
    {Icon && (
      <Icon
        sx={{
          mr: closed ? 0 : 1,
          ml: isSubItem ? 2 : 0,
          svg: {
            height: isSubItem ? iconSize - 2 : iconSize,
            width: isSubItem ? iconSize - 2 : iconSize,
            stroke: "#fff",
            strokeWidth: 1.5,
          },
        }}
        closed={closed}
      />
    )}
    {!Icon && <Box width={16} />}
  </ListItemIcon>
);

const NavLabel = ({ title, closed }) => (
  <ListItemText
    primary={title}
    sx={{ display: { md: 'none', lg: closed ? 'none' : 'block' }, m: 0 }}
  />
);

function NavItem({
  title,
  path,
  Icon,
  action,
  onAction,
  onNavigate,
  closed,
  compact,
  iconSize,
  isSubItem,
}) {
  const active = useActiveNavLink(path, true) && !action;

  return (
    <Tooltip title={compact ? title : ''} placement="right">
      <ListItemButton
        disableGutters
        sx={active ? { ...defaultLinkStyling, ...activeLinkStyling } : defaultLinkStyling}
        selected={active}
        {...(action
          ? { onClick: () => onAction(action) }
          : { component: RouterLink, to: path, onClick: onNavigate })}
      >
        <NavIcon Icon={Icon} closed={closed} iconSize={iconSize} isSubItem={isSubItem} />
        <NavLabel title={title} closed={closed} />
      </ListItemButton>
    </Tooltip>
  );
}

function NavGroup({ title, Icon, subItems, open, onOpen, onToggle, closed, compact, iconSize }) {
  const { pathname } = useLocation();
  const containsActive = subItems.some((item) => isPathActive(item.path, pathname));
  const listId = `nav-group-${title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <>
      <Box position="relative">
        <Tooltip title={compact ? title : ''} placement="right">
          <ListItemButton
            disableGutters
            sx={
              containsActive && !open
                ? { ...defaultLinkStyling, ...activeLinkStyling }
                : defaultLinkStyling
            }
            component={RouterLink}
            to={subItems[0].path}
            onClick={onOpen}
          >
            <NavIcon Icon={Icon} closed={closed} iconSize={iconSize} />
            <NavLabel title={title} closed={closed} />
            {!compact && <Box width={24} flexShrink={0} />}
          </ListItemButton>
        </Tooltip>
        <IconButton
          aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
          aria-expanded={open}
          aria-controls={listId}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onToggle();
          }}
          sx={{
            position: 'absolute',
            right: compact ? 0 : 8,
            top: '50%',
            transform: 'translateY(-50%)',
            p: 0.5,
          }}
        >
          <IconWrapper
            icon={open ? 'chevronUp' : 'chevronDown'}
            height={16}
            display="flex"
            sx={{ display: 'flex', svg: { height: 16, width: 16, stroke: '#fff', color: '#fff' } }}
          />
        </IconButton>
      </Box>
      <Collapse in={open}>
        <List disablePadding id={listId}>
          {subItems.map((item) => (
            <NavItem
              key={item.title + item.path}
              {...item}
              closed={closed}
              compact={compact}
              iconSize={iconSize}
              onNavigate={onOpen}
              isSubItem
            />
          ))}
        </List>
      </Collapse>
    </>
  );
}

export const VerticalNavBar = () => {
  const navItems = useNavItems();
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const { pathname } = useLocation();
  const [openGroup, setOpenGroup] = useState(null);
  const [iconSize, setIconSize] = useState(24);

  const { closeMenu, setCloseMenu } = useNavbar();
  const compact = closeMenu || !isLg;

  const activeGroupTitle = navItems.find((item) =>
    item.subItems?.some((subItem) => isPathActive(subItem.path, pathname))
  )?.title;

  useEffect(() => {
    setOpenGroup(activeGroupTitle ?? null);
  }, [activeGroupTitle, pathname]);

  const {
    account: { super_admin },
    user,
  } = useAuth();

  const { data: { company } = {} } = useGetCompanyById(user?.company_id?.toString(), {
    enabled: !super_admin,
  });

  const websiteURL = (super_admin || (company && company.Type === 'suppplier')) ? ROOT_PATHS.WEBSITE : ROOT_PATHS.WEBSITE_BUYERS;

  const handleAction = (action) => {
    if (action === 'toggleMenu') {
      setCloseMenu(prev => !prev);
    }
  };

  const handleCloseMenu = () => {
    setCloseMenu(!closeMenu);
    if (!closeMenu) {
      setIconSize(24);
    } else {
      setIconSize(24);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Box
        position="relative"
        height="100vh"
        maxHeight="100vh"
        width={{
          md: VERTICAL_NAV.closedWidth,
          lg: closeMenu ? VERTICAL_NAV.closedWidth : VERTICAL_NAV.width,
        }}
        minWidth={{
          md: VERTICAL_NAV.closedWidth,
          lg: closeMenu ? VERTICAL_NAV.closedWidth : VERTICAL_NAV.width,
        }}
      />
      <Stack
        zIndex={10}
        bgcolor="white"
        position="fixed"
        // height={(super_admin || user.Company.subscription_level === "gold") ? "100vh" : "calc(100% - 52px)"}
        // maxHeight={(super_admin || user.Company.subscription_level === "gold") ? "100vh" : "calc(100% - 52px)"}
        height={"100vh"}
        maxHeight={"100vh"}
        flexShrink={0}
        boxShadow={27}
        borderRight="1px solid"
        borderColor="border.main"
        component="nav"
        width={{
          md: VERTICAL_NAV.closedWidth,
          lg: closeMenu ? VERTICAL_NAV.closedWidth : VERTICAL_NAV.width,
        }}
        // top={(super_admin || user.Company.subscription_level === "gold") ? 0 : 52}
        top={0}
      >
        <Box p={1.5} height={45}>
          <Box
            height={1}
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            sx={{
              img: { borderRadius: 1 },
              '.small-logo': { display: { md: 'block', lg: closeMenu ? 'block' : 'none' } },
              '.large-logo': { display: { md: 'none', lg: closeMenu ? 'none' : 'block' } },
            }}
          >
            {
              //closeMenu &&
              <img
                className="small-logo"
                src={(super_admin || !company?.square_logo_url) ? DelegonSmallLogo : company?.square_logo_url}
                alt={'Delegon'}
                loading="lazy"
                style={{
                  height: super_admin ? 30 : "100%",
                  borderRadius: 0,
                }}
              />
            }
            {
              //!closeMenu &&
              (super_admin || !company?.lockup_logo_url) ? (
                <img
                  className="large-logo"
                  src={DelegonLogo}
                  alt={'Delegon'}
                  loading="lazy"
                  style={super_admin ? {
                    width: 150,
                    flexShrink: 0,
                    borderRadius: 0,
                  } : {
                    height: '62px',
                    width: '100%',
                    objectFit: 'contain',
                    borderRadius: 0,
                  }}
                />
                // <DelegonLogo className="large-logo" style={{ height: 62 }} />
              ) : (
                <img
                  className="large-logo"
                  src={company?.lockup_logo_url}
                  alt="company logo"
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover',
                  }}
                />
              )
            }
          </Box>
        </Box>

        <Stack
          sx={{
            overflowY: "auto"
          }}
        >
          <Box
            display={{ xs: 'none', lg: 'block' }}
            onClick={handleCloseMenu}
            position="absolute"
            right={-10}
            top={0}
            width={10}
            height={1}
            sx={{ cursor: 'pointer', opacity: 1 }}
          />
          <Box sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.primary' }}>
            <List sx={{p: 0}}>
              {navItems.map((navItem) =>
                navItem.subItems ? (
                  <NavGroup
                    key={navItem.title}
                    {...navItem}
                    open={openGroup === navItem.title}
                    onOpen={() => setOpenGroup(navItem.title)}
                    onToggle={() =>
                      setOpenGroup((prev) => (prev === navItem.title ? null : navItem.title))
                    }
                    closed={closeMenu}
                    compact={compact}
                    iconSize={iconSize}
                  />
                ) : (
                  <NavItem
                    key={`${navItem.title}${navItem.path || ''}`}
                    {...navItem}
                    onAction={handleAction}
                    onNavigate={() => setOpenGroup(null)}
                    closed={closeMenu}
                    compact={compact}
                    iconSize={iconSize}
                  />
                )
              )}
            </List>
          </Box>
        </Stack>
        <Stack flex={1} justifyContent="flex-end" sx={{bgcolor: 'background.primary'}}>
          <Box
            component={RouterLink}
            to={websiteURL}
            color="text.white"
            sx={{ textDecoration: 'none' }}
            target="_blank"
            rel="noopener"
          >
            <ListItemButton
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 2,
                pl: 3,
              }}
            >
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ display: { md: 'none', lg: closeMenu ? 'none' : 'block' } }}
              >
                Delegon.com
              </Typography>
              <img
                className="small-logo"
                src={DelegonSmallLogoWhite}
                alt={'Delegon'}
                loading="lazy"
                style={{
                  height: iconSize,
                }}
              />
            </ListItemButton>
          </Box>
          <Box
            component={RouterLink}
            to={paths.logout}
            color="text.white"
            sx={{ textDecoration: 'none' }}
          >
            <ListItemButton
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 2,
                pl: 3,
              }}
            >
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ display: { md: 'none', lg: closeMenu ? 'none' : 'block' } }}
              >
                Log out
              </Typography>
              <IconWrapper icon="logOut" sx={{svg: {stroke: "#fff", color: "#fff"}}} />
            </ListItemButton>
          </Box>
        </Stack>
      </Stack>
    </>
  );
};
